import { useEffect, useRef, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SHEET_W, SHEET_H } from "@/components/StudySheet";
import { VersionSheet, VERSIONS } from "@/components/Sheets";
import { exportSheetPdf } from "@/lib/pdf";
import { fmtDate } from "@/lib/api";

export const PdfButton = ({ work, version = "a", testId = "btn-download-pdf", className = "" }) => {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const go = async () => {
    setBusy(true);
    try {
      await new Promise((r) => setTimeout(r, 50));
      await exportSheetPdf(ref.current, `${work.title || work.mindmap?.title}_versione_${version.toUpperCase()}`);
      toast.success(`PDF Versione ${version.toUpperCase()} scaricato`);
    } catch { toast.error("Errore durante la creazione del PDF"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button data-testid={testId} onClick={go} disabled={busy} className={`inline-flex items-center gap-2 h-11 px-5 bg-[#0D9488] text-white text-sm font-semibold hover:bg-[#059669] disabled:opacity-50 transition-colors duration-150 ${className}`}>
        <Download className="w-4 h-4" /> {busy ? "Creazione PDF…" : `Scarica PDF ${version.toUpperCase()}`}
      </button>
      {busy && (
        <div style={{ position: "fixed", left: -20000, top: 0 }} aria-hidden>
          <VersionSheet ref={ref} work={work} version={version} />
        </div>
      )}
    </>
  );
};

export const SheetPreview = ({ work, version = "a" }) => {
  const box = useRef(null);
  const [scale, setScale] = useState(0.5);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setScale(e.contentRect.width / SHEET_W));
    ro.observe(box.current);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={box} className="w-full overflow-hidden border border-zinc-300 bg-white shadow-[0_1px_0_#18181B]" style={{ height: SHEET_H * scale }} data-testid={`study-sheet-preview-${version}`}>
      <div style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: SHEET_W }}>
        <VersionSheet work={work} version={version} still={false} />
      </div>
    </div>
  );
};

export const VersionTabs = ({ work, active, onSelect, loading = {}, onlyAvailable }) => (
  <div className="grid grid-cols-3 border border-zinc-200 bg-white" data-testid="version-tabs">
    {VERSIONS.filter((v) => !onlyAvailable || work[v.key]).map((v) => {
      const ready = !!work[v.key];
      return (
        <button key={v.id} data-testid={`version-tab-${v.id}`} onClick={() => onSelect(v.id)}
          className={`text-left px-4 sm:px-5 py-3 border-r last:border-r-0 border-zinc-200 transition-colors duration-150 ${active === v.id ? "bg-[#0F3D3E] text-white" : "hover:bg-zinc-50"}`}>
          <span className="block font-display font-bold tracking-tight">{v.label}</span>
          <span className={`block font-mono text-[10px] uppercase tracking-widest ${active === v.id ? "text-[#5EEAD4]" : "text-zinc-500"}`}>
            {loading[v.id] ? "Generazione…" : ready ? v.sub : `${v.sub} · da generare`}
          </span>
        </button>
      );
    })}
  </div>
);

const WorkBody = ({ work }) => {
  const [v, setV] = useState("a");
  return (
    <>
      <DialogHeader className="min-w-0 px-6 pt-6 pb-4 border-b border-zinc-200 flex-row items-end justify-between gap-4 space-y-0">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
            {work.author} · {work.classe} · {fmtDate(work.created_at, true)}
          </p>
          <DialogTitle className="font-display text-2xl font-bold tracking-tight">{work.title}</DialogTitle>
          <DialogDescription className="sr-only">Anteprima delle versioni della scheda di studio</DialogDescription>
        </div>
        <PdfButton work={work} version={v} testId="viewer-download-pdf-btn" />
      </DialogHeader>
      <div className="p-6 space-y-6 min-w-0">
        <VersionTabs work={work} active={v} onSelect={setV} onlyAvailable />
        <SheetPreview key={v} work={work} version={v} />
        <section className="border border-zinc-200 p-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-zinc-600 mb-3">Testo estratto</p>
          <p className="text-sm leading-relaxed text-zinc-700 whitespace-pre-wrap max-h-64 overflow-y-auto" data-testid="viewer-extracted-text">{work.extracted_text}</p>
        </section>
      </div>
    </>
  );
};

export const WorkViewer = ({ work, onClose }) => (
  <Dialog open={!!work} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-[1280px] w-[96vw] max-h-[92vh] overflow-y-auto rounded-none p-0" data-testid="work-viewer-dialog">
      {work && <WorkBody key={work.id} work={work} />}
    </DialogContent>
  </Dialog>
);
